/**
 * Class ModelCache
 *
 * This is the card catalogue of all game information
 * Each game's data is stored in a hash based on game id a la 'gid_2014_05_29_texmlb_minmlb_1'
 */
define(function (require, exports, module) {
    // dependency require imports
    var exists = require('extant/extant');
    var MessageService = require('message-service/message-service');
    var dataFetcher = require('data-fetcher/data-fetcher');
    var logg = require('../../utils/core-log');

    // project level imports
    var SubsManager = require('../../control/SubscriptionManager');
    var TfsManager = require('../../control/TfsManager');
    var ApiItemModelCache = require('./ApiItemModelCache');
    var Events = require('../../enums/DataEvents');

    var STRING = 'string';
    var TSPATH = 'metaData.timeStamp';
    var COMMA = ',';
    var DATASOURCENAME = 'dataSourceName';

    function ModelCache (gameID) {
        this.gameID = gameID;
        this.apiItemModelCache = new ApiItemModelCache(gameID);
        this.datasourceMeta = {};
    }

    ModelCache.prototype = {
        /**
         * Function retrieveGameData
         * @param gameID {String}
         * @param datasources {*} can be string, array, object or comma-separated string of sources
         * @returns {Object} a collectiong of just the Datasources requested
         */
        retrieveGameData: function () {
            return this.apiItemModelCache;
        },
        /**
         * Function storeLegacyGameData
         * @param gameID {String} game the data's associated with
         * @param datasource {String} the name (from the Enums) of the Load source
         * @param data {Object} the value object modelled from the loaded data
         *
         * This function files each value object modelled after Load data in it's proper place
         * Sorted by game id and datasource
         */
        storeGameData: function (datasourceResponse) {
            if (!exists(datasourceResponse, DATASOURCENAME)) {
                logg.log('you must pass a datasource name type to store it');
                return;
            }
            var datasourceName = datasourceResponse.dataSourceName;
            var data = datasourceResponse.data;
            var gameItemModel = this.apiItemModelCache.data;
            var datasourceMeta = this.datasourceMeta;

            logg.log('storing ' + datasourceName + ' for game ' + this.gameID);
            gameItemModel[datasourceName] = data;
            datasourceMeta[datasourceName] = {
                params: datasourceResponse.params
            };
        },

        expireDatasources: function (datasources) {
            var i = 0;
            var datasrc;
            var datasrcItem;
            var datasrcItemMeta;
            var datasourceList = this.getDatasourceList(datasources);
            var gameItemData = this.apiItemModelCache.data;
            var datasourceMeta = this.datasourceMeta;
            var len = datasourceList.length;
            var tfsActive = TfsManager.syncActive();

            for (; i < len; i++) {
                datasrc = datasourceList[i];
                datasrcItem = gameItemData[datasrc];
                datasrcItemMeta = datasourceMeta[datasrc] || {};

                if (exists(datasrcItem)) {
                    if (tfsActive) {
                        dataFetcher.clearDataSource({
                            dataSourceName: datasrc,
                            params: datasrcItemMeta.params
                        });
                    }

                    dataFetcher.expire({
                        dataSourceName: datasrc,
                        params: datasrcItemMeta.params
                    });
                }
            }
        },

        getDatasourceList: function (datasources) {
            var datasourceList = exists(datasources) ? datasources : SubsManager.getSubscriptions();

            if (typeof datasourceList === STRING) {
                datasourceList = datasourceList.split(COMMA);
            }
            return datasourceList;
        },

        storeApiData: function (response) {
            var previousTimecode;
            var newTimecode;
            var badDataUpdate = false;
            var datasourceMeta = this.datasourceMeta;
            var datasourceName = response.dataSourceName;
            var data = response.data;
            var gameSet = this.apiItemModelCache;
            var gameSetData = gameSet.data;
            var params = response.params;

            logg.log('storing API DATA result for game ' + this.gameID, response, datasourceMeta);

            var toExpire = datasourceMeta[datasourceName];

            //  Need to sanity check data due to
            //  diff/patch api issues
            if (exists(gameSetData.api, TSPATH)) {
                previousTimecode = gameSetData.api.metaData.timeStamp;
            }
            gameSet.applyNewData(data);

            // Check again with new data (see above)
            if (exists(gameSetData.api, TSPATH)) {
                newTimecode = gameSetData.api.metaData.timeStamp;
            }
            if (toExpire) {
                dataFetcher.clearDataSource({
                    dataSourceName: datasourceName,
                    params: toExpire.params,
                    refresh: toExpire.refresh
                });
            }
            datasourceMeta[datasourceName] = {
                params: params,
                refresh: response.refresh
            };

            // Confirm new timecode is different
            if (gameSetData.newData === true && gameSet.patched && newTimecode &&
                (newTimecode === previousTimecode || !TfsManager.compareTimecodes(previousTimecode, newTimecode))
            ) {
                badDataUpdate = true;
            }
            if (gameSet.patchQC === false || badDataUpdate === true) {
                MessageService.publish(Events.PATCH_ERROR, {
                    game: this.gameID,
                    live: !TfsManager.syncActive(),
                    startTimecode: previousTimecode,
                    endTimecode: newTimecode,
                    data: data
                });
                return false;
            }
            gameSetData.tfsTimecode = params.endTimecode || params.startTimecode || newTimecode;
            return true;
        }
    };

    return ModelCache;
});
